import { useEffect, useState } from 'react';
import { getAggregations, type AggregationRow } from '../lib/api';
import { theme } from '../theme';

export function AggregationsPage() {
  const [rows, setRows] = useState<AggregationRow[]>([]);
  const [windowDays, setWindowDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'totalScans' | 'successRate' | 'failureScans'>('totalScans');

  useEffect(() => {
    setLoading(true);
    getAggregations(windowDays)
      .then((res) => setRows(res.rows))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [windowDays]);

  const sorted = [...rows].sort((a, b) => {
    if (sortBy === 'successRate') return a.successRate - b.successRate;
    return b[sortBy] - a[sortBy];
  });

  return (
    <div>
      <h1>Aggregations</h1>
      <p style={{ color: theme.text.secondary, fontSize: 13 }}>
        Per-vehicle-class scan counts + success rates. Click a header to sort.
      </p>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <label style={{ color: theme.text.secondary, fontSize: 13 }}>Window:</label>
        <select value={windowDays} onChange={(e) => setWindowDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
      </div>

      {error && <p style={{ color: theme.status.error }}>Error: {error}</p>}
      {loading && <p>Loading…</p>}
      {!loading && rows.length === 0 && <p>No data in this window.</p>}
      {!loading && rows.length > 0 && (
        <table style={{ width: '100%' }}>
          <thead>
            <tr style={{ background: theme.surface.header }}>
              <Th>Vehicle</Th>
              <SortableTh active={sortBy === 'totalScans'} onClick={() => setSortBy('totalScans')}>
                Total scans
              </SortableTh>
              <Th>Successes</Th>
              <SortableTh
                active={sortBy === 'failureScans'}
                onClick={() => setSortBy('failureScans')}
              >
                Failures
              </SortableTh>
              <SortableTh
                active={sortBy === 'successRate'}
                onClick={() => setSortBy('successRate')}
              >
                Success rate
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr
                key={row.vehicleKey}
                style={{ borderBottom: `1px solid ${theme.surface.border}` }}
              >
                <Td>
                  {row.make ?? '?'} {row.model ?? '?'} {row.modelYear ?? ''}
                  <div style={{ color: theme.text.tertiary, fontSize: 11 }}>{row.vehicleKey}</div>
                </Td>
                <Td>{row.totalScans}</Td>
                <Td>{row.successScans}</Td>
                <Td>{row.failureScans}</Td>
                <Td>
                  <span
                    style={{
                      color:
                        row.successRate > 0.8
                          ? theme.status.success
                          : row.successRate > 0.5
                            ? theme.status.warning
                            : theme.status.error,
                    }}
                  >
                    {(row.successRate * 100).toFixed(0)}%
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: theme.text.secondary,
        fontWeight: 600,
      }}
    >
      {children}
    </th>
  );
}
function SortableTh({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <th
      onClick={onClick}
      style={{
        textAlign: 'left',
        padding: '8px 12px',
        fontSize: 12,
        color: active ? theme.brand.primary : theme.text.secondary,
        fontWeight: 600,
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      {children} {active && '▼'}
    </th>
  );
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={{ padding: '8px 12px', fontSize: 13 }}>{children}</td>;
}
